<template>
  <article
    class="benefits-points-reward-card"
    :class="{
      'is-active': reward.active,
      'is-inactive': !reward.active,
      'is-readonly': readonly,
    }"
    @click="handleClick"
  >
    <div v-if="hasImage" class="benefits-points-reward-image-container">
      <img :src="reward.image.url" alt="" class="benefits-points-reward-image" />

      <div v-if="!readonly" class="benefits-points-reward-image-overlay">
        <Icon icon-class="ui-edit-05" size="10" color="#ffffff" />
      </div>
    </div>

    <div class="benefits-points-reward-content">
      <div class="benefits-points-reward-header">
        <div class="benefits-points-reward-cost">
          {{ reward.points || 0 }} {{ t("benefitsCenter-points-pointsLabel") }}
        </div>
      </div>
      <div class="benefits-points-reward-footer-row">
        <div class="benefits-points-reward-info">
          <strong>{{ reward.name || 'Sem nome' }}</strong>
          <span>{{ reward.subtitle || '' }}</span>
        </div>

        <div v-if="!readonly" class="benefits-points-reward-status-control" @click.stop>
          <label class="benefits-points-toggle-row">
            <span class="benefits-points-status-label">{{ t("status") }}</span>
            <BenefitsToggle v-model="reward.active" @input="handleStatusChange" />
          </label>
        </div>
      </div>
    </div>
  </article>
</template>

<script>
import Icon from "../../../components/icon/icon";
import BenefitsToggle from "../../shared/benefits-toggle.vue";

export default {
  name: "BenefitsRewardCard",
  components: {
    Icon,
    BenefitsToggle,
  },
  props: {
    reward: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    hasImage() {
      return Boolean(this.reward.image && this.reward.image.url);
    },
  },
  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    handleClick() {
      if (!this.readonly) {
        this.$emit("click", this.reward);
      }
    },
    handleStatusChange() {
      this.$emit("save-reward", this.reward);
    },
  },
};
</script>

<style lang="scss" scoped>
.benefits-points-reward-card {
  align-items: stretch;
  background: #ffffff;
  border: 1px solid #2aaa4c;
  border-radius: 16px;
  cursor: pointer;
  display: flex;
  gap: 16px;
  padding: 12px;
  position: relative;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  width: 100%;

  &.is-active {
    border-color: #2aaa4c;
  }

  &.is-inactive {
    background: #f9fafb;
    border-color: #e5e7eb;
    opacity: 0.92;

    .benefits-points-reward-cost {
      background: #f3f4f6;
      color: #9da4ae;
    }

    .benefits-points-reward-info {
      strong,
      span {
        color: #9da4ae;
      }
    }

    .benefits-points-reward-image {
      filter: grayscale(1);
      opacity: 0.72;
    }

    .benefits-points-status-label {
      color: #9da4ae;
    }
  }

  &:hover:not(.is-readonly) {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
  }

  &.is-readonly {
    cursor: default;
    border-color: #2aaa4c;
  }
}

.benefits-points-reward-image-container {
  border-radius: 12px;
  flex: 0 0 120px;
  height: 120px;
  overflow: hidden;
  position: relative;
  width: 120px;

  .benefits-points-reward-card:not(.is-readonly) &:hover .benefits-points-reward-image-overlay {
    opacity: 1;
  }
}

.benefits-points-reward-image {
  height: 100%;
  object-fit: cover;
  width: 100%;
}

.benefits-points-reward-image-overlay {
  align-items: center;
  background: rgba(17, 25, 39, 0.6);
  color: #ffffff;
  display: flex;
  inset: 0;
  justify-content: center;
  opacity: 0;
  position: absolute;
  transition: opacity 0.3s ease-in-out;
}

.benefits-points-reward-content {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  justify-content: space-between;
  min-width: 0;
  padding: 4px 0;
  text-align: left;
}

.benefits-points-reward-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.benefits-points-reward-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;

  strong {
    color: #4d5761;
    font-family: "Sora", sans-serif;
    font-size: 18px;
    font-weight: 400;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
    display: block;
  }

  span {
    color: #4d5761;
    font-family: "Inter", sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
    display: block;
  }
}

.benefits-points-reward-cost {
  align-items: center;
  background: #d3f8e0;
  border-radius: 8px;
  color: #2aaa4c;
  display: inline-flex;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  gap: 4px;
  padding: 4px 12px;
  width: fit-content;
}

.benefits-points-reward-footer-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  width: 100%;
  gap: 16px;
}

.benefits-points-reward-status-control {
  display: flex;
  align-items: center;
}

.benefits-points-status-label {
  color: #4d5761;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 500;
  margin-right: 8px;
  margin-bottom: 2px;
}

.benefits-points-toggle-row {
  align-items: center;
  align-self: flex-start;
  color: #384250;
  display: flex;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  gap: 8px;
  line-height: 1.2;
  margin: 0;
}

.benefits-points-toggle-control {
  display: inline-flex;
}

.benefits-points-toggle-input {
  height: 1px;
  opacity: 0;
  position: absolute;
  width: 1px;
}

.benefits-points-toggle {
  background: #ffffff;
  border: 0.5px solid #f3f4f6;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  height: 18px;
  padding: 1px 4px;
  position: relative;
  width: 40px;

  &::before {
    background: #d2d6db;
    border: 0.5px solid #9da4ae;
    border-radius: 3px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
    content: "";
    height: 14px;
    left: 2px;
    position: absolute;
    top: 1px;
    transition: transform 0.15s ease;
    width: 16px;
  }
}

.benefits-points-toggle-input:checked + .benefits-points-toggle {
  background: #d3f8e0;
  border-color: #2aaa4c;

  &::before {
    background: #2aaa4c;
    border-color: #097347;
    transform: translateX(18px);
  }
}

.benefits-points-toggle-input:focus-visible + .benefits-points-toggle {
  outline: 2px solid rgba(42, 170, 76, 0.35);
  outline-offset: 2px;
}

@media (max-width: 767px) {
  .benefits-points-reward-card {
    flex-direction: column;
    align-items: flex-start;
    padding: 16px;
  }

  .benefits-points-reward-image-container {
    width: 100%;
    height: 140px;
    flex: 0 0 140px;
  }

  .benefits-points-reward-content {
    width: 100%;
    gap: 12px;
  }

  .benefits-points-reward-footer-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .benefits-points-reward-status-control {
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }
}
</style>
